import {
  ContributionBreakdown,
  EducationCosts,
  NeedEstimate,
  NormalizedStudentProfile,
  PolicyConfig,
} from "./types";

function roundToTwo(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface NeedCalculationResult {
  contributions: ContributionBreakdown;
  need: NeedEstimate;
}

// Formula: Need = Total Education Costs − Total Student Resources
export function estimateNeed(
  profile: NormalizedStudentProfile,
  costs: EducationCosts,
  policy: PolicyConfig
): NeedCalculationResult {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { financial, derived, study, exemptions } = profile;

  // Fixed student contribution 
  const fixedStudentContribution = exemptions.fixedContributionWaived
    ? 0
    : policy.fixedStudentContribution;

  // Income above (exemption × terms) 
  const incomeExemption =
    policy.studentIncomeExemptionPerTerm * study.numberOfTerms;
  const assessableIncome = Math.max(0, financial.studentIncome - incomeExemption);
  const variableStudentContribution =
    assessableIncome * policy.variableStudentContributionRate;

  let parentContribution = 0;
  if (derived.parentInfoRequired && derived.combinedParentIncome > 0) {
    const familySize =
      2 + (profile.parents.dependentChildrenInFamily ?? 1);
    const livingAllowance =
      policy.parentBaseLivingAllowance +
      Math.max(0, familySize - 2) * policy.parentLivingAllowancePerMember;

    const discretionaryIncome = Math.max(
      0,
      derived.combinedParentIncome - livingAllowance
    );

    const childrenInPSE = Math.max(
      1,
      profile.parents.childrenInPostsecondary ?? 1
    );

    parentContribution =
      (discretionaryIncome * policy.parentDiscretionaryRate) / childrenInPSE;
  }

  const spouseContribution = derived.spouseInfoRequired
    ? derived.combinedSpouseIncome * policy.spouseContributionRate
    : 0;

  // Asset contribution 
  const assessableAssets = Math.max(
    0,
    financial.studentAssets - policy.assetExemptionFloor
  );
  const assetContribution = assessableAssets * policy.assetContributionRate;

  // Scholarships / bursaries 
  // First $1,800 is exempt; only the excess reduces aid
  const scholarshipExemption = 1800;
  const scholarshipsAwards = Math.max(
    0,
    financial.scholarshipsAwards - scholarshipExemption
  );

  // Government income 
  const governmentIncome = financial.governmentIncome;

  // Dependant support reduction 
  const dependantReduction =
    derived.totalDependantCount * policy.dependantSupportBonus;

  // Total contribution 
  const rawContribution =
    fixedStudentContribution +
    variableStudentContribution +
    parentContribution +
    spouseContribution +
    assetContribution +
    scholarshipsAwards +
    governmentIncome;

  const totalContribution = Math.max(0, rawContribution - dependantReduction);

  // Calculated need 
  const calculatedNeed = Math.max(0, costs.totalCosts - totalContribution);

  return {
    contributions: {
      fixedStudentContribution: roundToTwo(fixedStudentContribution),
      variableStudentContribution: roundToTwo(variableStudentContribution),
      parentContribution: roundToTwo(parentContribution),
      spouseContribution: roundToTwo(spouseContribution),
      scholarshipsAwards: roundToTwo(scholarshipsAwards),
      governmentIncome: roundToTwo(governmentIncome),
      totalContribution: roundToTwo(totalContribution),
    },
    need: {
      totalCosts: roundToTwo(costs.totalCosts),
      totalContribution: roundToTwo(totalContribution),
      calculatedNeed: roundToTwo(calculatedNeed),
      unmetNeedFloor: 0,
    },
  };
}